import { useAuth } from '../auth/useAuth';
import { LogoutButton } from '../components/auth/AuthButtons';
import {
  EdmsProvisionStatus,
  useEdmsProvisioningState,
} from '../modules/app/edmsProvisioningState';

const formatProvisioningStatus = (status: EdmsProvisionStatus) => {
  switch (status) {
    case 'created':
    case 'already_exists':
      return 'Provisioned';
    case 'in_progress':
      return 'Provisioning';
    case 'cooldown':
      return 'Retry scheduled';
    case 'role_not_found':
      return 'Role missing';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    default:
      return 'Unknown';
  }
};

export function AccountPage() {
  const { user, isAuthenticated } = useAuth();
  const provisioningState = useEdmsProvisioningState();

  return (
    <div className='mx-auto w-full max-w-4xl'>
      {/* Page Header */}
      <div className='mb-8 bg-white px-6 py-4'>
        <h1 className='size-h2 text-primary font-bold'>Account</h1>
        <p className='size-p2 text-secondary mt-2'>
          Manage your profile information and account settings
        </p>
      </div>

      {isAuthenticated && user ? (
        <div className='space-y-6 px-6 pb-8'>
          {/* Profile Information Card */}
          <div
            className='border-border-secondary bg-surface-primary rounded-lg
              border p-6'
          >
            <h2 className='size-h4 text-primary mb-6 font-medium'>
              Profile Information
            </h2>
            <dl className='grid gap-4 sm:grid-cols-2'>
              <div>
                <dt
                  className='size-caption text-tertiary mb-1 font-medium
                    tracking-wide uppercase'
                >
                  Name
                </dt>
                <dd className='size-p1 text-primary'>{user.name}</dd>
              </div>
              <div>
                <dt
                  className='size-caption text-tertiary mb-1 font-medium
                    tracking-wide uppercase'
                >
                  Email
                </dt>
                <dd className='size-p1 text-primary'>{user.email}</dd>
              </div>
              <div>
                <dt
                  className='size-caption text-tertiary mb-1 font-medium
                    tracking-wide uppercase'
                >
                  Role
                </dt>
                <dd className='size-p1 text-primary'>
                  <span
                    className='bg-surface-focus size-p2 text-inverted
                      inline-flex items-center rounded-md px-2.5 py-0.5
                      font-medium'
                  >
                    {user.role}
                  </span>
                </dd>
              </div>
              <div>
                <dt
                  className='size-caption text-tertiary mb-1 font-medium
                    tracking-wide uppercase'
                >
                  Provider
                </dt>
                <dd className='size-p1 text-primary'>{user.provider}</dd>
              </div>
            </dl>
          </div>

          {/* Account Actions Card */}
          <div
            className='border-border-secondary bg-surface-primary rounded-lg
              border p-6'
          >
            <h2 className='size-h4 text-primary mb-4 font-medium'>
              Account Actions
            </h2>
            <LogoutButton />
          </div>

          {/* EDMS Provisioning Card */}
          <div
            className='border-border-secondary bg-surface-primary rounded-lg
              border p-6'
          >
            <h2 className='size-h4 text-primary mb-4 font-medium'>
              EDMS Provisioning
            </h2>
            <dl className='grid gap-4 sm:grid-cols-2'>
              <div>
                <dt
                  className='size-caption text-tertiary mb-1 font-medium
                    tracking-wide uppercase'
                >
                  Status
                </dt>
                <dd className='size-p1 text-primary'>
                  {formatProvisioningStatus(provisioningState.status)}
                </dd>
              </div>
              <div>
                <dt
                  className='size-caption text-tertiary mb-1 font-medium
                    tracking-wide uppercase'
                >
                  Last Updated
                </dt>
                <dd className='size-p1 text-primary'>
                  {provisioningState.updatedAt
                    ? new Date(provisioningState.updatedAt).toLocaleString()
                    : '—'}
                </dd>
              </div>
              <div className='sm:col-span-2'>
                <dt
                  className='size-caption text-tertiary mb-1 font-medium
                    tracking-wide uppercase'
                >
                  Details
                </dt>
                <dd className='size-p1 text-primary'>
                  {provisioningState.status === 'already_exists' ||
                  provisioningState.status === 'created'
                    ? 'Provisioning complete.'
                    : provisioningState.message ?? 'No details available.'}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      ) : (
        <div
          className='border-border-secondary bg-surface-primary mx-6 rounded-lg
            border p-8 text-center'
        >
          <p className='size-p1 text-secondary'>Not authenticated</p>
        </div>
      )}
    </div>
  );
}
